import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import Button from '../../../components/base/Button';
import { Link } from 'react-router-dom';

export default function PostSurgicalCare() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/services/post-surgical-care#webpage`,
    url: `${siteUrl}/services/post-surgical-care`,
    name: 'Post-Surgical Care at Home Las Vegas | Celebrity Home Health',
    description: 'Expert post-surgical home care in Las Vegas — wound care, medication management, therapy, and recovery support after surgery. Medicare certified, ACHC accredited.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    medicalSpecialty: 'Surgical',
    lastReviewed: '2026-04-11',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: 'Post-Surgical Care', item: `${siteUrl}/services/post-surgical-care` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'Post-Surgical Care Las Vegas | Home Recovery After Surgery';
  }, []);

  const surgicalPrograms = [
    {
      icon: 'ri-wheelchair-line',
      title: 'Hip/Knee Replacement Recovery at Home',
      description: 'Comprehensive orthopedic rehabilitation program designed specifically for hip and knee replacement patients in Las Vegas homes.',
      image: 'https://public.readdy.ai/ai/img_res/5ac4c683b9e8f2c1e0cf36c1ddd03e3f.jpg',
      benefits: [
        'Personalized mobility restoration program',
        'Pain management and medication oversight',
        'Fall prevention and home safety assessment',
        'Progressive strength and flexibility training',
        'Gait training and walking assistance',
        'Post-operative wound care monitoring'
      ],
      timeline: '6-12 weeks typical recovery period',
      specialists: 'Physical Therapists, Occupational Therapists, Skilled Nurses'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Cardiac Surgery Recovery at Home',
      description: 'Specialized cardiac rehabilitation program for patients recovering from heart surgery, bypass procedures, and cardiac interventions.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20cardiac%20nurse%20monitoring%20elderly%20patient%20recovering%20from%20heart%20surgery%20in%20comfortable%20home%20bedroom%2C%20cardiac%20rehabilitation%20at%20home%2C%20clean%20white%20interior%20with%20medical%20monitoring%20equipment%2C%20nurse%20checking%20vital%20signs%20after%20cardiac%20surgery&width=500&height=350&seq=cardiac-recovery-lv&orientation=landscape',
      benefits: [
        'Cardiac monitoring and vital sign tracking',
        'Heart-healthy lifestyle education',
        'Medication management for cardiac drugs',
        'Activity progression and exercise guidance',
        'Dietary counseling for heart health',
        'Emergency response protocols'
      ],
      timeline: '8-16 weeks comprehensive program',
      specialists: 'Cardiac Nurses, Physical Therapists, Dietitians'
    },
    {
      icon: 'ri-first-aid-kit-line',
      title: 'General Surgical Wound Care at Home',
      description: 'Expert wound care management for all types of surgical incisions and post-operative healing in Las Vegas homes.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20nurse%20providing%20surgical%20wound%20care%20and%20dressing%20change%20to%20patient%20in%20bright%20modern%20home%20bedroom%2C%20post-operative%20wound%20management%20at%20home%2C%20clean%20white%20interior%20with%20medical%20supplies%2C%20skilled%20nursing%20wound%20care%20in%20residential%20setting&width=500&height=350&seq=wound-care-recovery-lv&orientation=landscape',
      benefits: [
        'Sterile wound care and dressing changes',
        'Infection prevention and monitoring',
        'Surgical site assessment and documentation',
        'Pain management protocols',
        'Patient and family education',
        'Coordination with surgical team'
      ],
      timeline: '2-8 weeks depending on surgery type',
      specialists: 'Wound Care Nurses, Skilled Nurses'
    },
    {
      icon: 'ri-capsule-line',
      title: 'Post-Surgical Pain Management at Home',
      description: 'Comprehensive pain management protocols for optimal comfort and healing during post-surgical recovery.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20nurse%20helping%20patient%20with%20pain%20management%20techniques%20in%20comfortable%20home%20living%20room%2C%20post-surgical%20pain%20relief%20at%20home%2C%20clean%20white%20interior%20with%20natural%20lighting%2C%20nurse%20providing%20comfort%20care%20in%20residential%20setting&width=500&height=350&seq=pain-management-lv&orientation=landscape',
      benefits: [
        'Medication management and scheduling',
        'Non-pharmacological pain relief techniques',
        'Pain assessment and monitoring',
        'Side effect management',
        'Alternative therapy coordination',
        'Family caregiver training'
      ],
      timeline: 'Customized based on individual needs',
      specialists: 'Pain Management Nurses, Clinical Pharmacists'
    }
  ];

  const recoverySteps = [
    {
      step: '1',
      title: 'Pre-Discharge Planning',
      description: 'Coordination with hospital discharge team and surgical staff to ensure seamless transition home.',
      icon: 'ri-hospital-line'
    },
    {
      step: '2',
      title: 'Initial Home Assessment',
      description: 'Comprehensive evaluation of home environment and patient needs within 24-48 hours.',
      icon: 'ri-home-heart-line'
    },
    {
      step: '3',
      title: 'Personalized Care Plan',
      description: 'Development of individualized recovery program based on surgery type and patient goals.',
      icon: 'ri-file-list-3-line'
    },
    {
      step: '4',
      title: 'Active Recovery Phase',
      description: 'Implementation of skilled nursing care, therapy services, and progress monitoring.',
      icon: 'ri-user-heart-line'
    },
    {
      step: '5',
      title: 'Independence Restoration',
      description: 'Gradual transition to independence with ongoing support and safety monitoring.',
      icon: 'ri-trophy-line'
    }
  ];

  const conditions = [
    'Orthopedic surgeries (joint replacements, fracture repairs)',
    'Cardiac procedures (bypass, valve repair, stent placement)',
    'Abdominal surgeries (gallbladder, hernia, appendix)',
    'Thoracic procedures (lung, chest wall surgeries)',
    'Neurological surgeries (spine, brain procedures)',
    'Vascular surgeries (arterial, venous procedures)',
    'Urological procedures (kidney, bladder surgeries)',
    'General surgical procedures (skin, soft tissue)'
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={jsonLd as Record<string, unknown>} id="json-ld-post-surgical" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-300/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-6xl font-bold mb-6">Post-Surgical Care at Home Las Vegas</h1>
              <p className="text-xl text-white/90 leading-relaxed mb-8">
                Comprehensive <strong>post-surgical recovery programs</strong> delivered in your Las Vegas home. 
                Professional nursing care, rehabilitation therapy, and specialized recovery support for orthopedic, 
                cardiac, and general surgical procedures across Henderson, Summerlin, and North Las Vegas.
              </p>
              <div className="bg-teal-900/30 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-teal-700/30">
                <h3 className="text-xl font-bold mb-3">Specialized Recovery Programs:</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-teal-100">
                  <span>• Hip/Knee replacement recovery</span>
                  <span>• Cardiac surgery recovery</span>
                  <span>• General surgical wound care</span>
                  <span>• Pain management protocols</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-teal-600 hover:bg-gray-50 shadow-2xl whitespace-nowrap">
                    Start Post-Surgical Care
                  </Button>
                </Link>
                <a href="tel:866-939-0330">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-teal-600 shadow-2xl whitespace-nowrap">
                    Call: 866-939-0330
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20healthcare%20team%20providing%20comprehensive%20post-surgical%20care%20in%20Las%20Vegas%20home%2C%20skilled%20nurses%20and%20therapists%20helping%20patient%20recover%20from%20surgery%20in%20comfortable%20residential%20setting%20with%20modern%20medical%20equipment%20and%20bright%20interior%20design&width=500&height=400&seq=post-surgical-hero-lv&orientation=landscape"
                alt="Post-Surgical Care at Home Las Vegas - Recovery Programs"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Surgical Recovery Programs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Post-Surgical Recovery Programs</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized <strong>post-surgical care at home</strong> programs designed for optimal recovery 
              and healing in the comfort of your Las Vegas home.
            </p>
          </div>

          <div className="space-y-20">
            {surgicalPrograms.map((program, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                      <i className={`${program.icon} text-white text-3xl`}></i>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">{program.title}</h3>
                  </div>
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                    {program.description}
                  </p>
                  <div className="mb-8">
                    <h4 className="text-xl font-bold text-gray-900 mb-4">Recovery Benefits:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {program.benefits.map((benefit, benefitIndex) => (
                        <div key={benefitIndex} className="flex items-center bg-teal-50 rounded-lg p-3 border border-teal-100/50">
                          <i className="ri-check-line text-teal-600 mr-3 text-lg"></i>
                          <span className="text-gray-700 font-medium">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100/50">
                      <h5 className="font-bold text-gray-900 mb-2">Recovery Timeline</h5>
                      <p className="text-teal-700 font-semibold">{program.timeline}</p>
                    </div>
                    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100/50">
                      <h5 className="font-bold text-gray-900 mb-2">Care Team</h5>
                      <p className="text-teal-700 font-semibold">{program.specialists}</p>
                    </div>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative">
                    <img
                      src={program.image}
                      alt={`${program.title} - Post-Surgical Recovery Las Vegas`}
                      className="rounded-2xl shadow-2xl object-cover w-full h-96"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recovery Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Post-Surgical Recovery Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach to ensure optimal healing and successful recovery at home
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-teal-200 to-cyan-400 rounded-full"></div>
            
            <div className="space-y-16">
              {recoverySteps.map((step, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-teal-100">
                      <div className={`flex items-center mb-4 ${index % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                        <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                          <i className={`${step.icon} text-white text-xl`}></i>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                      <span className="text-white font-bold text-xl">{step.step}</span>
                    </div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Surgical Conditions We Treat */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Surgical Procedures We Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive post-surgical care for a wide range of surgical procedures and conditions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {conditions.map((condition, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-teal-200">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3 shadow-md">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{condition}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Choose Home Recovery in Las Vegas?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The advantages of recovering from surgery in your own home with professional care
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <i className="ri-home-heart-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Comfort of Home</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Recover in your familiar environment with family support and personal comfort items nearby.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <i className="ri-shield-check-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Reduced Infection Risk</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Lower risk of hospital-acquired infections while receiving professional medical care at home.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <i className="ri-money-dollar-circle-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cost-Effective Care</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                More affordable than extended hospital stays while maintaining high-quality professional care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-teal-900/30 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-80 h-80 bg-teal-600/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-600/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold mb-8">
                Ready for Post-Surgical Recovery at Home?
              </h2>
              <p className="text-xl text-gray-300 mb-10 leading-relaxed">
                Contact Celebrity Home Health today to learn how our comprehensive <strong>post-surgical care programs</strong> 
                can support your recovery journey in the comfort of your Las Vegas home.
              </p>
              <div className="flex flex-col sm:flex-row gap-6">
                <Link to="/contact">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-teal-600 hover:bg-gray-50 shadow-2xl whitespace-nowrap">
                    Start Your Recovery Program
                  </Button>
                </Link>
                <a href="tel:866-939-0330">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-teal-600 shadow-2xl whitespace-nowrap">
                    Call: 866-939-0330
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-3xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Happy%20patient%20recovering%20from%20surgery%20at%20home%20with%20professional%20healthcare%20team%20in%20Las%20Vegas%2C%20successful%20post-surgical%20recovery%20in%20comfortable%20residential%20setting%2C%20patient%20smiling%20with%20nurse%20and%20therapist%20in%20bright%20home%20environment&width=500&height=400&seq=cta-recovery-success-lv&orientation=landscape"
                  alt="Post-Surgical Care Success - Las Vegas Home Recovery"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
